// addClickGuard.js
//
// Filtra el clic FANTASMA (el 2º onClick que un doble-tap accidental o un doble-disparo del
// navegador emiten sobre el MISMO botón) sin comerse los clics rápidos DELIBERADOS del
// operario de barra. Módulo puro (como pricing.js): testeable con Jest sin renderizar las
// 2.500 líneas de Dispenser.jsx.
//
// Reemplaza a la ventana anti doble-tap de 350 ms, que descartaba clics buenos en silencio:
// rotando entre 2-3 productos se volvía al primero dentro de esa ventana y el add se perdía.
//
// El fix estructural del clic fantasma es `touch-manipulation` en el botón (elimina el
// double-tap-zoom, su fuente física). Esta guarda es solo el cinturón sobre el tirante:
// - ventana muy corta (60 ms): un doble-disparo fantasma llega en < 50 ms; un humano no
//   repite el MISMO botón en menos de ~120 ms;
// - refuerzo por posición: ignorar solo si además el clic aterriza en el píxel EXACTO del
//   anterior (el fantasma cae en el mismo punto; un dedo real, no).
//
// ⛔ PROHIBIDO usar `event.detail > 1` como criterio de rechazo: en escritorio, 10 clics
//    rápidos en el mismo píxel dan detail 1, 2, 3… — rechazaría JUSTO lo que queremos aceptar.
//    Se deja documentado para que nadie lo "arregle" en el futuro.

export const GHOST_CLICK_WINDOW_MS = 60;

/**
 * Crea una guarda con memoria del último clic por producto.
 * @returns {{ shouldIgnore: (productId: string, event?: object) => boolean }}
 */
export function createAddGuard() {
  // { [productId]: { ts, x, y } } — último clic aceptado de cada producto.
  const last = {};

  function shouldIgnore(productId, event) {
    // Sin evento (llamadas internas: elección de ubicación, merge del carrito pendiente) no
    // es input humano → nunca se filtra.
    if (!event) return false;

    const id = productId == null ? '' : String(productId).toLowerCase();
    // event.timeStamp puede ser 0 legítimamente (primer evento tras cargar): solo caemos a
    // Date.now() si no es un número finito (evento sintético sin reloj).
    const now = Number.isFinite(event.timeStamp) ? event.timeStamp : Date.now();
    const x = typeof event.clientX === 'number' ? event.clientX : null;
    const y = typeof event.clientY === 'number' ? event.clientY : null;

    const prev = last[id];
    const withinWindow = prev && (now - prev.ts) < GHOST_CLICK_WINDOW_MS;
    // Mismo píxel EXACTO (si el evento no trae coordenadas, no exigimos posición: la ventana
    // temporal sola decide).
    const samePixel = prev && (
      x == null || y == null || prev.x == null || prev.y == null
        ? true
        : prev.x === x && prev.y === y
    );

    // ⛔ NO: `if (event.detail > 1) return true;`  ← rechazaría los clics rápidos buenos.

    if (withinWindow && samePixel) {
      // Es el fantasma: no actualizamos `last` (no arrastramos la ventana con cada rebote).
      return true;
    }

    last[id] = { ts: now, x, y };
    return false;
  }

  return { shouldIgnore };
}
